import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { OrganizationAdminDetails } from 'types/admin';

interface OrganizationChartsProps {
  organization: OrganizationAdminDetails;
}

const OrganizationCharts: React.FC<OrganizationChartsProps> = ({ organization }) => {
  const { t } = useTranslation();
  // Prepare aggregated data based on period
  const prepareChartData = () => {
    const { chartData, periodInfo } = organization;
    const { period } = periodInfo;
    
    
    // Helper function to get aggregation key based on period
    const getAggregationKey = (dateString: string, periodType: string) => {
      const date = new Date(dateString);
      switch (periodType) {
        case 'week':
        case 'two_weeks':
        case 'month':
          // For week, two_weeks, month periods, use daily aggregation
          return date.toISOString().split('T')[0]; // YYYY-MM-DD format
        case 'quarter':
        case 'semester':
        case 'year':
          // For quarter, semester, year periods, use monthly aggregation
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        default:
          return date.toISOString().split('T')[0];
      }
    };
    
    // Helper function to process data with current/previous period separation
    const processChartData = (data: Array<{ date: string; currentCount: number; previousCount: number; count: number }>, periodType: string) => {
      return data.map(item => {
        let label = '';
        
        if (['week', 'two_weeks', 'month'].includes(periodType)) {
          // Daily labels for week, two_weeks, month periods
          const date = new Date(item.date);
          label = date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric' 
          });
        } else {
          // Monthly labels for quarter, semester, year periods
          const date = new Date(item.date + '-01');
          label = date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short' 
          });
        }
        
        return {
          key: item.date,
          currentCount: item.currentCount,
          previousCount: item.previousCount,
          count: item.count, // Total for backward compatibility
          label,
          periodType: 'current' // This will be used for styling
        };
      });

    };
    
    return {
      projects: processChartData(chartData.projects, period),
      revisions: processChartData(chartData.revisions, period),
      transactions: processChartData(chartData.transactions, period)
    };
  };

  const chartData = prepareChartData();

  // Get period label
  const getPeriodLabel = () => {
    const { period } = organization.periodInfo;
    switch (period) {
      case 'week':
        return t('ADMIN_STATS_PERIOD_WEEK');
      case 'two_weeks':
        return t('ADMIN_STATS_PERIOD_TWO_WEEKS');
      case 'month':
        return t('ADMIN_STATS_PERIOD_MONTH');
      case 'quarter':
        return t('ADMIN_STATS_PERIOD_QUARTER');
      case 'semester':
        return t('ADMIN_STATS_PERIOD_SEMESTER');
      case 'year':
        return t('ADMIN_STATS_PERIOD_YEAR');
      default:
        return t('ADMIN_STATS_CURRENT_PERIOD');
    }
  };

  // Calculate totals and period data
  const getChartTotals = () => {
    const { projectProgression, revisionProgression, transactionProgression } = organization;
    
    return {
      projects: {
        total: projectProgression.currentPeriodCount + projectProgression.previousPeriodCount,
        current: projectProgression.currentPeriodCount,
        previous: projectProgression.previousPeriodCount,
        progression: projectProgression.progressionPercentage
      },
      revisions: {
        total: revisionProgression.currentPeriodCount + revisionProgression.previousPeriodCount,
        current: revisionProgression.currentPeriodCount,
        previous: revisionProgression.previousPeriodCount,
        progression: revisionProgression.progressionPercentage
      },
      transactions: {
        total: transactionProgression.currentPeriodCount + transactionProgression.previousPeriodCount,
        current: transactionProgression.currentPeriodCount,
        previous: transactionProgression.previousPeriodCount,
        progression: transactionProgression.progressionPercentage
      }
    };
  };

  const totals = getChartTotals();

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0]?.payload;
      const currentData = payload.find(p => p.dataKey === 'currentCount');
      const previousData = payload.find(p => p.dataKey === 'previousCount');
      const lineData = payload.find(p => p.dataKey === 'cumulative');
      
      return (
        <Box sx={{ 
          backgroundColor: 'white', 
          border: '1px solid #ccc', 
          p: 1, 
          borderRadius: 1,
          boxShadow: 2
        }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {dataPoint?.label || label}
          </Typography>
          {currentData && (
            <Typography variant="caption" sx={{ color: '#1976d2', display: 'block', fontWeight: 'bold' }}>
              {t('ADMIN_STATS_CURRENT')} {getPeriodLabel()}: {currentData.value}
            </Typography>
          )}
          {previousData && (
            <Typography variant="caption" sx={{ color: '#757575', display: 'block', fontWeight: 'bold' }}>
              {t('ADMIN_STATS_PREVIOUS')} {getPeriodLabel()}: {previousData.value}
            </Typography>
          )}
          {currentData && previousData && (
            <Typography variant="caption" sx={{ 
              color: currentData.value > previousData.value ? '#4caf50' : currentData.value < previousData.value ? '#f44336' : '#757575',
              display: 'block',
              fontWeight: 'bold',
              mt: 0.5
            }}>
              {currentData.value > previousData.value ? '↗' : currentData.value < previousData.value ? '↘' : '→'} 
              {Math.abs(currentData.value - previousData.value)} 
              {currentData.value > previousData.value ? ' more' : currentData.value < previousData.value ? ' less' : ' same'} than previous period
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  const formatProgression = (percentage: number) => {
    if (percentage === 0) return '0%';
    if (percentage > 0) return `+${percentage.toFixed(1)}%`;
    return `${percentage.toFixed(1)}%`;
  };

  const getProgressionColor = (percentage: number) => {
    if (percentage > 0) return '#4caf50';
    if (percentage < 0) return '#f44336';
    return '#757575';
  };

  return (
    <Grid container spacing={2}>
      {/* Projects Chart */}
      <Grid item xs={12} md={4}>
        <Card sx={{ height: 300 }}>
          <CardContent sx={{ height: '100%', p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ color: '#ed6c02', fontWeight: 'bold' }}>
                {t('ADMIN_STATS_PROJECT_PROGRESSION')}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: getProgressionColor(totals.projects.progression),
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                }}
                dir="ltr"
              >
                {formatProgression(totals.projects.progression)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }} >
                {getPeriodLabel()}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('ADMIN_STATS_PREVIOUS')}: {totals.projects.previous}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('ADMIN_STATS_CURRENT')}: {totals.projects.current}
                </Typography>
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData.projects} margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="label" 
                  tick={{ fontSize: 11 }}
                  angle={0}
                  textAnchor="middle"
                  height={100}
                  interval={0}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  width={15}
                />
                <Tooltip content={<CustomTooltip />} />
                                <Bar 
                  dataKey="currentCount" 
                  fill="#ed6c02"
                  radius={[2, 2, 0, 0]}
                  opacity={0.8}
                  name={t('ADMIN_STATS_CURRENT')}
                  stackId="stack"
                />
                <Bar 
                  dataKey="previousCount" 
                  fill="#ffb74d"
                  radius={[0, 0, 2, 2]}
                  opacity={0.6}
                  name={t('ADMIN_STATS_PREVIOUS')}
                  stackId="stack"
                />
                <Line 
                  type="monotone" 
                  dataKey="cumulative" 
                  stroke="#ff9800" 
                  strokeWidth={2}
                  dot={{ fill: '#ff9800', strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Revisions Chart */}
      <Grid item xs={12} md={4}>
        <Card sx={{ height: 300 }}>
          <CardContent sx={{ height: '100%', p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ color: '#9c27b0', fontWeight: 'bold' }}>
                {t('ADMIN_STATS_REVISION_PROGRESSION')}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: getProgressionColor(totals.revisions.progression),
                  fontWeight: 'bold',
                  fontSize: '0.875rem'
                }}
                dir="ltr"
              >
                {formatProgression(totals.revisions.progression)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {getPeriodLabel()}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('ADMIN_STATS_PREVIOUS')}: {totals.revisions.previous}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('ADMIN_STATS_CURRENT')}: {totals.revisions.current}
                </Typography>
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData.revisions} margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="label" 
                  tick={{ fontSize: 11 }}
                  angle={0}
                  textAnchor="middle"
                  height={100}
                  interval={0}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  width={15}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="currentCount" 
                  fill="#9c27b0"
                  radius={[2, 2, 0, 0]}
                  opacity={0.8}
                  name={t('ADMIN_STATS_CURRENT')}
                  stackId="stack"
                />
                <Bar 
                  dataKey="previousCount" 
                  fill="#ce93d8"
                  radius={[0, 0, 2, 2]}
                  opacity={0.6}
                  name={t('ADMIN_STATS_PREVIOUS')}
                  stackId="stack"
                />
                <Line 
                  type="monotone" 
                  dataKey="cumulative" 
                  stroke="#ba68c8" 
                  strokeWidth={2}
                  dot={{ fill: '#ba68c8', strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Transactions Chart */}
      <Grid item xs={12} md={4}>
        <Card sx={{ height: 300 }}>
          <CardContent sx={{ height: '100%', p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
                {t('ADMIN_STATS_CREDITS_PROGRESSION')} 
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: getProgressionColor(totals.transactions.progression),
                  fontWeight: 'bold',
                  fontSize: '0.875rem'
                }}
                dir="ltr"
              >
                {formatProgression(totals.transactions.progression)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {getPeriodLabel()}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('ADMIN_STATS_PREVIOUS')}: {totals.transactions.previous}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('ADMIN_STATS_CURRENT')}: {totals.transactions.current}
                </Typography>
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData.transactions} margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="label" 
                  tick={{ fontSize: 11 }}
                  angle={0}
                  textAnchor="middle"
                  height={100}
                  interval={0}
                />
                <YAxis  
                  tick={{ fontSize: 11 }}
                  width={15}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="currentCount" 
                  fill="#d32f2f"
                  radius={[2, 2, 0, 0]}
                  opacity={0.8}
                  name={t('ADMIN_STATS_CURRENT')}
                  stackId="stack"
                />
                <Bar 
                  dataKey="previousCount" 
                  fill="#ef5350"
                  radius={[0, 0, 2, 2]}
                  opacity={0.6}
                  name={t('ADMIN_STATS_PREVIOUS')}
                  stackId="stack"
                />
                <Line 
                  type="monotone" 
                  dataKey="cumulative" 
                  stroke="#ef5350" 
                  strokeWidth={2}
                  dot={{ fill: '#ef5350', strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default OrganizationCharts; 